import JSZip from "jszip";
import { fromArrayBuffer } from "geotiff";
import type { LandCoverChangeResult } from "@app/features/msa/msaTypes";

/**
 * Recursively find MSA_LU.tif in a zip archive.
 * Zip structure may have the file at root or in a subfolder.
 */
function findMsaLuInZip(zip: JSZip): JSZip.JSZipObject | null {
  const match = zip.file(/MSA_LU\.tif$/i);
  if (Array.isArray(match) && match.length > 0) {
    return match[0];
  }
  return null;
}

/**
 * Fetch, unzip, parse GeoTIFFs and calculate Project Area and Area of Land Cover Change.
 * Mirrors the Python rasterio / R terra logic.
 */
export async function calculateLandCoverChange(
  baselineZipUrl: string,
  projectZipUrl: string
): Promise<Omit<LandCoverChangeResult, "baselineJobId" | "projectJobId">> {
  console.log("[LCC] Starting calculation", { baselineZipUrl, projectZipUrl });

  // 1. Download both zips in parallel
  const [baselineArrayBuffer, projectArrayBuffer] = await Promise.all([
    fetch(baselineZipUrl, { cache: "no-store" }).then((r) => {
      if (!r.ok) throw new Error(`Failed to fetch baseline: ${r.statusText}`);
      return r.arrayBuffer();
    }),
    fetch(projectZipUrl, { cache: "no-store" }).then((r) => {
      if (!r.ok) throw new Error(`Failed to fetch project: ${r.statusText}`);
      return r.arrayBuffer();
    }),
  ]);

  // 2. Unzip and extract MSA_LU.tif from each
  const [baselineZip, projectZip] = await Promise.all([
    JSZip.loadAsync(baselineArrayBuffer),
    JSZip.loadAsync(projectArrayBuffer),
  ]);

  const tifFileB = findMsaLuInZip(baselineZip);
  const tifFileWp = findMsaLuInZip(projectZip);

  console.log("[LCC] Found MSA_LU.tif", {
    baselineFound: !!tifFileB,
    projectFound: !!tifFileWp,
  });

  if (!tifFileB) {
    throw new Error("MSA_LU.tif not found in baseline zip");
  }
  if (!tifFileWp) {
    throw new Error("MSA_LU.tif not found in project zip");
  }

  const [tifBufferB, tifBufferWp] = await Promise.all([
    tifFileB.async("arraybuffer"),
    tifFileWp.async("arraybuffer"),
  ]);

  // 3. Parse GeoTIFFs
  const [bGeoTiff, wpGeoTiff] = await Promise.all([
    fromArrayBuffer(tifBufferB as ArrayBuffer),
    fromArrayBuffer(tifBufferWp as ArrayBuffer),
  ]);

  const [bImage, wpImage] = await Promise.all([
    bGeoTiff.getImage(0),
    wpGeoTiff.getImage(0),
  ]);

  // 4. Read raster data and metadata
  const resolution = bImage.getResolution();
  const resX = resolution[0];
  const resY = resolution[1];
  const pixelAreaHa = (resX * Math.abs(resY)) / 10000;

  console.log("[LCC] Resolution and pixel area", {
    resX,
    resY,
    pixelAreaHa,
  });

  const [bRasters, wpRasters] = await Promise.all([
    bImage.readRasters({ samples: [0] }),
    wpImage.readRasters({ samples: [0] }),
  ]);

  const bRaster = (Array.isArray(bRasters) ? bRasters[0] : (bRasters as unknown as { [k: number]: ArrayLike<number> })[0]) as ArrayLike<number>;
  const wpRaster = (Array.isArray(wpRasters) ? wpRasters[0] : (wpRasters as unknown as { [k: number]: ArrayLike<number> })[0]) as ArrayLike<number>;

  // Get nodata values (geotiff v3 uses fileDirectory)
  let bNoData: number | null = null;
  let wpNoData: number | null = null;
  try {
    if (bImage.fileDirectory.hasTag("GDAL_NODATA" as never)) {
      const val = bImage.fileDirectory.getValue("GDAL_NODATA" as never);
      if (typeof val === "string") bNoData = parseFloat(val);
    }
  } catch {
    // Tag may not exist or be deferred
  }
  try {
    if (wpImage.fileDirectory.hasTag("GDAL_NODATA" as never)) {
      const val = wpImage.fileDirectory.getValue("GDAL_NODATA" as never);
      if (typeof val === "string") wpNoData = parseFloat(val);
    }
  } catch {
    // Tag may not exist or be deferred
  }

  // 5. Convert to Float64Array and handle nodata
  const len = bRaster.length;
  if (wpRaster.length !== len) {
    throw new Error("Baseline and project rasters have different dimensions");
  }

  console.log("[LCC] Raster lengths", {
    baselineLength: len,
    projectLength: wpRaster.length,
  });

  const bData = new Float64Array(bRaster);
  const wpData = new Float64Array(wpRaster);

  if (bNoData != null) {
    for (let i = 0; i < len; i++) {
      if (bData[i] === bNoData) bData[i] = NaN;
    }
  }
  if (wpNoData != null) {
    for (let i = 0; i < len; i++) {
      if (wpData[i] === wpNoData) wpData[i] = NaN;
    }
  }

  // 6. Compute areas (same logic as Python)
  let validCount = 0;
  let lucCount = 0;
  for (let i = 0; i < len; i++) {
    const bVal = bData[i];
    const diff = bVal - wpData[i];
    if (Number.isFinite(bVal)) validCount++;
    if (Number.isFinite(diff) && diff !== 0) lucCount++;
  }

  const projectAreaHa = validCount * pixelAreaHa;
  const lucAreaHa = lucCount * pixelAreaHa;

  console.log("[LCC] Computed areas", {
    projectAreaHa,
    lucAreaHa,
    validCount,
    lucCount,
  });

  return {
    projectAreaHa,
    lucAreaHa,
  };
}
